import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Grid,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar
} from '@mui/material'
import {
  LibraryBooks,
  Edit,
  Delete,
  Search,
  Add,
  CloudUpload,
  Download,
  Visibility,
  PictureAsPdf,
  Description,
  Image,
  VideoLibrary,
  Article
} from '@mui/icons-material'
import axios from 'axios'

const BaseConocimiento = () => {
  const [documentos, setDocumentos] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategoria, setSelectedCategoria] = useState('')
  const [openDialog, setOpenDialog] = useState(false)
  const [selectedDoc, setSelectedDoc] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploading, setUploading] = useState(false)
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    categoria: 'manual',
    tags: '',
    archivo: null,
    publico: false
  })

  const categorias = [
    { value: 'manual', label: 'Manual', icon: <Description /> },
    { value: 'guia', label: 'Guía', icon: <Article /> },
    { value: 'tutorial', label: 'Tutorial', icon: <VideoLibrary /> },
    { value: 'plantilla', label: 'Plantilla', icon: <Description /> },
    { value: 'procedimiento_interno', label: 'Procedimiento Interno', icon: <Article /> },
    { value: 'formato', label: 'Formato', icon: <Description /> },
    { value: 'politica_interna', label: 'Política Interna', icon: <Article /> },
    { value: 'buenas_practicas', label: 'Buenas Prácticas', icon: <LibraryBooks /> },
    { value: 'capacitacion', label: 'Material de Capacitación', icon: <VideoLibrary /> },
    { value: 'referencia', label: 'Material de Referencia', icon: <LibraryBooks /> }
  ]

  useEffect(() => {
    loadDocumentos()
  }, [])

  const loadDocumentos = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get('/api/base-conocimiento', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setDocumentos(response.data)
    } catch (error) {
      console.error('Error:', error)
      // Datos de ejemplo mientras se implementa el backend
      setDocumentos([
        {
          id: 1,
          titulo: 'Manual del Sistema de Gestión de Calidad',
          descripcion: 'Manual completo para el uso del SGC, incluyendo procedimientos, formularios y mejores prácticas',
          categoria: 'manual',
          tags: 'SGC,calidad,manual,procedimientos',
          archivo_url: '/uploads/conocimiento/manual_sgc.pdf',
          archivo_nombre: 'manual_sgc.pdf',
          archivo_tamano: 2458671,
          archivo_tipo: 'application/pdf',
          publico: true,
          descargas: 45,
          created_at: '2024-01-15T10:30:00Z'
        },
        {
          id: 2,
          titulo: 'Guía de Buenas Prácticas Docentes',
          descripcion: 'Documento con recomendaciones y estrategias para mejorar la práctica docente',
          categoria: 'buenas_practicas',
          tags: 'docencia,educacion,buenas practicas,pedagogia',
          archivo_url: '/uploads/conocimiento/guia_docentes.pdf',
          archivo_nombre: 'guia_docentes.pdf',
          archivo_tamano: 1234567,
          archivo_tipo: 'application/pdf',
          publico: false,
          descargas: 23,
          created_at: '2024-02-01T14:45:00Z'
        },
        {
          id: 3,
          titulo: 'Plantilla de Informe de Auditoría',
          descripcion: 'Plantilla estándar para reportes de auditorías internas del SGC',
          categoria: 'plantilla',
          tags: 'auditoria,plantilla,reporte,SGC',
          archivo_url: '/uploads/conocimiento/plantilla_auditoria.docx',
          archivo_nombre: 'plantilla_auditoria.docx',
          archivo_tamano: 87543,
          archivo_tipo: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          publico: true,
          descargas: 67,
          created_at: '2024-01-28T09:15:00Z'
        }
      ])
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setSelectedDoc(null)
    setFormData({
      titulo: '',
      descripcion: '',
      categoria: 'manual',
      tags: '',
      archivo: null,
      publico: false
    })
    setOpenDialog(true)
  }

  const handleEdit = (doc) => {
    setSelectedDoc(doc)
    setFormData({
      titulo: doc.titulo,
      descripcion: doc.descripcion,
      categoria: doc.categoria,
      tags: doc.tags,
      archivo: null,
      publico: doc.publico
    })
    setOpenDialog(true)
  }

  const handleSave = async () => {
    try {
      setUploading(true)
      const token = localStorage.getItem('token')
      
      const formDataToSend = new FormData()
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null) {
          formDataToSend.append(key, formData[key])
        }
      })
      
      if (selectedDoc) {
        // Editar
        await axios.put(`/api/base-conocimiento/${selectedDoc.id}`, formDataToSend, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            setUploadProgress(progress)
          }
        })
        setSuccess('Documento actualizado exitosamente')
      } else {
        // Crear
        await axios.post('/api/base-conocimiento', formDataToSend, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            setUploadProgress(progress)
          }
        })
        setSuccess('Documento creado exitosamente')
      }
      
      setOpenDialog(false)
      loadDocumentos()
    } catch (error) {
      setError('Error al guardar: ' + (error.response?.data?.detail || error.message))
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const handleDelete = async (doc) => {
    if (!window.confirm(`¿Está seguro de eliminar el documento "${doc.titulo}"?`)) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      await axios.delete(`/api/base-conocimiento/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSuccess('Documento eliminado exitosamente')
      loadDocumentos()
    } catch (error) {
      setError('Error al eliminar: ' + (error.response?.data?.detail || error.message))
    }
  }

  const getCategoriaLabel = (categoria) => {
    const cat = categorias.find(c => c.value === categoria)
    return cat ? cat.label : categoria
  }

  const getCategoriaIcon = (categoria) => {
    const cat = categorias.find(c => c.value === categoria)
    return cat ? cat.icon : <Description />
  }

  const getCategoriaColor = (categoria) => {
    switch (categoria) {
      case 'manual': return 'primary'
      case 'guia': return 'secondary'
      case 'tutorial': return 'info'
      case 'plantilla': return 'warning'
      case 'formato': return 'warning'
      case 'procedimiento_interno': return 'error'
      case 'politica_interna': return 'error'
      case 'buenas_practicas': return 'success'
      case 'capacitacion': return 'info'
      case 'referencia': return 'default'
      default: return 'default'
    }
  }

  const getFileIcon = (tipo) => {
    if (tipo?.includes('pdf')) return <PictureAsPdf sx={{ color: 'error.main' }} />
    if (tipo?.includes('word') || tipo?.includes('document')) return <Description sx={{ color: 'primary.main' }} />
    if (tipo?.includes('image')) return <Image sx={{ color: 'secondary.main' }} />
    if (tipo?.includes('video')) return <VideoLibrary sx={{ color: 'info.main' }} />
    return <Description />
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    setFormData({ ...formData, archivo: file })
  }

  const filteredDocumentos = documentos.filter(doc => {
    const matchesSearch = doc.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.descripcion.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.tags.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategoria = !selectedCategoria || doc.categoria === selectedCategoria
    return matchesSearch && matchesCategoria
  })

  if (loading) {
    return (
      <Box>
        <Typography>Cargando base de conocimiento...</Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Base de Conocimiento
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreate}
        >
          Nuevo Documento
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Buscar por título, descripción o tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1 }}
          />
          
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Categoría</InputLabel>
            <Select
              value={selectedCategoria}
              label="Categoría"
              onChange={(e) => setSelectedCategoria(e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              {categorias.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {filteredDocumentos.map((doc) => (
          <Grid item xs={12} md={6} lg={4} key={doc.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                  <Avatar sx={{ bgcolor: `${getCategoriaColor(doc.categoria)}.main`, width: 32, height: 32 }}>
                    {getCategoriaIcon(doc.categoria)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" component="div" sx={{ lineHeight: 1.2 }}>
                      {doc.titulo}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Chip
                        label={getCategoriaLabel(doc.categoria)}
                        color={getCategoriaColor(doc.categoria)}
                        size="small"
                      />
                      {doc.publico && (
                        <Chip label="Público" color="success" variant="outlined" size="small" />
                      )}
                    </Box>
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" paragraph>
                  {doc.descripcion}
                </Typography>

                {doc.tags && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Tags: {doc.tags.split(',').map(tag => tag.trim()).join(' • ')}
                    </Typography>
                  </Box>
                )}

                {doc.archivo_nombre && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                    {getFileIcon(doc.archivo_tipo)}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" display="block">
                        {doc.archivo_nombre}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(doc.archivo_tamano)} • {doc.descargas} descargas
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>

              <CardActions sx={{ justifyContent: 'space-between' }}>
                <Box>
                  {doc.archivo_url && (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => window.open(doc.archivo_url, '_blank')}
                        title="Ver documento"
                        color="primary"
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const link = document.createElement('a')
                          link.href = doc.archivo_url
                          link.download = doc.archivo_nombre
                          link.click()
                        }}
                        title="Descargar"
                        color="secondary"
                      >
                        <Download />
                      </IconButton>
                    </>
                  )}
                </Box>
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(doc)}
                    title="Editar"
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(doc)}
                    title="Eliminar"
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </CardActions>
            </Card>
          </Grid>
        ))}

        {filteredDocumentos.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No se encontraron documentos en la base de conocimiento
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Dialog para crear/editar */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedDoc ? 'Editar Documento' : 'Nuevo Documento'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Título del Documento"
              value={formData.titulo}
              onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
              required
            />
            
            <TextField
              fullWidth
              label="Descripción"
              multiline
              rows={3}
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              required
            />

            <FormControl fullWidth>
              <InputLabel>Categoría</InputLabel>
              <Select
                value={formData.categoria}
                label="Categoría"
                onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
              >
                {categorias.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {cat.icon}
                      {cat.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Tags"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder="Separar tags con comas (ej: manual, SGC, calidad)"
              helperText="Tags para facilitar la búsqueda del documento"
            />

            <Box>
              <input
                accept="*"
                style={{ display: 'none' }}
                id="archivo-upload"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="archivo-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  fullWidth
                >
                  {formData.archivo ? formData.archivo.name : 'Seleccionar archivo'}
                </Button>
              </label>
              {uploading && (
                <LinearProgress 
                  variant="determinate" 
                  value={uploadProgress} 
                  sx={{ mt: 1 }} 
                />
              )}
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                ¿Documento público?
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Los documentos públicos pueden ser vistos por todos los usuarios, los privados solo por administradores y editores.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  variant={!formData.publico ? 'contained' : 'outlined'}
                  onClick={() => setFormData({ ...formData, publico: false })}
                  size="small"
                >
                  Privado
                </Button>
                <Button
                  variant={formData.publico ? 'contained' : 'outlined'}
                  onClick={() => setFormData({ ...formData, publico: true })}
                  size="small"
                >
                  Público
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={!formData.titulo || !formData.descripcion || uploading}
          >
            {uploading ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default BaseConocimiento